import { css } from '@emotion/react';
import { graphql } from 'gatsby';
import React from 'react';
import SvgSafeGatsbyImage from '../../../atoms/SvgSafeGatsbyImage';
import Container from '../../../atoms/Container';
import DotImage from '../../../../images/dot-pattern.svg';
import Accordion from '../../../atoms/styled-accordion';
import RichTextEditorStyles from '../../../utils/RichTextEditorStyles';
import paddingDefault from '../../../utils/paddingDefault';
import { StyledButtonAsLink } from '../../../primitives/styled-button';
import { breakpoints } from '../../../primitives/tokens';
import { FaqsData } from '../types/faqsTypes';

interface MediaContentImage {
    filename?: string;
    title?: string;
    description?: string;
    url?: string;
    contentStackAsset?: unknown;
}

interface MediaContentData {
    content?: string;
    disable_background_dots?: boolean;
    image?: MediaContentImage;
}

interface MediacontentFaqsProps {
    mediacontent: MediaContentData;
    faqs: FaqsData;
}

const styles = {
    containerInnerWrap: css({
        display: 'flex',
        flexDirection: 'row',
        [breakpoints.mb]: {
            display: 'block',
        },
    }),
    richText: css({
        color: '#fff',
        'h2, h4, li': {
            color: '#fff',
        },
    }),
    richTextWrap: css({
        paddingRight: '20px',
        paddingLeft: '40px',
        paddingTop: '160px',
        paddingBottom: '160px',
        [breakpoints.mb]: {
            paddingLeft: '56px',
            paddingRight: '56px',
            paddingTop: '0',
            paddingBottom: '0',
        },
    }),
    accordionWrap: css({
        paddingRight: '28px',
        width: '50%',
        [breakpoints.mb]: {
            paddingRight: '0',
            width: '100%',
        },
    }),
    buttonWrap: css({
        display: 'flex',
        marginTop: '48px',
        [breakpoints.mb]: {
            marginTop: '40px',
        },
    }),
    contentWrap: css({
        position: 'relative',
        marginBottom: '32px',
        height: '100%',
        paddingLeft: '8px',
        marginRight: '80px',
        width: '45%',
        [breakpoints.mb]: {
            marginBottom: '0',
            paddingLeft: '0',
            marginRight: '0',
            width: '100%',
        },
    }),
    dotPattern: css({
        display: 'block',
        position: 'absolute',
        top: '-48px',
        left: '-64px',
        width: '100%',
        height: '100%',
        [breakpoints.mb]: {
            display: 'none',
        },
    }),
    image: css({
        objectFit: 'scale-down',
        objectPosition: 'center',
        maxHeight: '100%',
        [breakpoints.mb]: {
            maxHeight: 'none',
        },
    }),
    imageWrapper: css({
        top: '0',
        position: 'absolute',
        width: '100%',
        maxWidth: 'none',
        height: '100%',
        [breakpoints.tb]: {
            maxWidth: '448px',
            position: 'relative',
        },
        [breakpoints.mb]: {
            position: 'relative',
            width: 'auto',
            height: 'auto',
        },
        img: css({
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            objectFit: 'contain !important' as any,
            objectPosition: 'center',
        }),
    }),
};

const MediacontentFaqs = ({ mediacontent, faqs }: MediacontentFaqsProps): JSX.Element => {
    const { link, heading, byline, questions } = faqs;
    const { content, image, disable_background_dots } = mediacontent;

    const hasImage = !!image?.url;

    const transformedData = {
        settings: faqs.settings,
        heading,
        byline,
        questions,
        link,
    };

    return (
        <Container>
            <div css={[paddingDefault(), styles.containerInnerWrap]}>
                <div css={styles.contentWrap}>
                    <div css={styles.richTextWrap}>
                        {content && (
                            <div
                                css={[RichTextEditorStyles, styles.richText]}
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{ __html: content }}
                            />
                        )}
                    </div>
                    {hasImage && (
                        <div css={styles.imageWrapper}>
                            <SvgSafeGatsbyImage
                                css={styles.image}
                                image={image}
                                alt={image.description}
                            />
                            {!disable_background_dots && <DotImage css={styles.dotPattern} />}
                        </div>
                    )}
                </div>
                <div css={styles.accordionWrap}>
                    <Accordion
                        faqs={transformedData}
                        onMediaContent
                    />
                    {link?.href && (
                        <div css={styles.buttonWrap}>
                            <StyledButtonAsLink to={link.href}>{link.title}</StyledButtonAsLink>
                        </div>
                    )}
                </div>
            </div>
        </Container>
    );
};

export const query = graphql`
  fragment MediacontentFaqsData on CmsPageBuilderBlocks {
    mediacontent_faqs {
      mediacontent {
        content
        disable_background_dots
        image {
          filename
          title
          description
          url
          contentStackAsset(width: 1440)
        }
      }
      faqs {
        heading
        questions {
          questions {
            question
            answer
          }
        }
        link {
          href
          title
        }
      }
    }
  }
`;

export default MediacontentFaqs;
